import React, { useEffect, useState, useRef } from "react";
import T from '../../components/common/T';
import { useParams, useNavigate } from "react-router-dom";
import { useVendors } from "../../context/VendorContext";
import { useAuth } from "../../context/AuthContext";
import { formatDate } from "../../utils/dateUtils";
import Swal from 'sweetalert2';
import {
  BuildingStorefrontIcon,
  ArrowLeftIcon,
  PencilSquareIcon,
  TrashIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  IdentificationIcon,
  DocumentCheckIcon,
  CalendarDaysIcon,
  StarIcon,
  ShieldCheckIcon,
  BriefcaseIcon
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";

export default function VendorDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const loadedIdRef = useRef(null);
  const { fetchVendorById, deleteVendor } = useVendors();
  const { checkAccess } = useAuth();

  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    if (loadedIdRef.current === String(id)) return;
    loadedIdRef.current = String(id);

    const loadData = async () => {
      try {
        setLoading(true);
        if (fetchVendorById) {
          const data = await fetchVendorById(id);
          setVendor(data);
        }
      } catch (err) {
        console.error("Failed to load vendor details", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, fetchVendorById]);

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this vendor!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e11d48',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete vendor!'
    });

    if (result.isConfirmed) {
      try {
        if (deleteVendor) {
          await deleteVendor(id);
        }
        Swal.fire('Deleted!', 'Vendor has been deleted.', 'success');
        navigate('/admin/VendorPage');
      } catch (err) {
        Swal.fire('Error!', 'Failed to delete vendor.', 'error');
      }
    }
  };

  const getStatusBadge = (status) => {
    const s = (status || 'Active').toLowerCase();
    if (s === 'active') return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/60';
    if (s === 'inactive') return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
    if (s === 'blacklisted') return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800/60';
    return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800/60';
  };

  if (loading) {
    return (
      <div className="p-8 max-w-6xl mx-auto flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <div className="w-10 h-10 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-xs text-gray-500 font-medium">Loading vendor profile...</p>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="p-8 max-w-4xl mx-auto text-center py-20">
        <BuildingStorefrontIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-lg font-bold text-gray-800 dark:text-white">Vendor Not Found</h2>
        <p className="text-xs text-gray-500 mt-1 mb-6">The vendor record with ID #{id} could not be located.</p>
        <button
          onClick={() => navigate('/admin/VendorPage')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 transition shadow-sm"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Back to Vendors
        </button>
      </div>
    );
  }

  const rating = Number(vendor.rating) || 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
      {/* Top Breadcrumb & Actions Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-gray-200 dark:border-[#30363d]">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/VendorPage')}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-[#30363d] bg-white dark:bg-[#161b22] text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-[#21262d] transition"
            title="Back to Vendors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-gray-100 dark:bg-[#21262d] text-gray-600 dark:text-slate-400 border border-gray-200 dark:border-[#30363d]">
                ID: {vendor.id}
              </span>
              <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${getStatusBadge(vendor.vendorStatus)}`}>
                {vendor.vendorStatus || 'Active'}
              </span>
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white mt-1">
              {vendor.vendorName}
            </h1>
            {vendor.companyName && (
              <p className="text-xs text-gray-500 dark:text-slate-400">
                {vendor.companyName}
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 self-end sm:self-auto">
          {checkAccess('vendor.manage') && (
            <>
              <button
                onClick={() => navigate(`/admin/VendorPage/edit/${vendor.id}`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 hover:bg-amber-100 text-xs font-semibold transition"
              >
                <PencilSquareIcon className="w-3.5 h-3.5" />
                <T>Edit Vendor</T>
              </button>
              <button
                onClick={handleDelete}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-200 dark:border-rose-800/60 bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 hover:bg-rose-100 text-xs font-semibold transition"
              >
                <TrashIcon className="w-3.5 h-3.5" />
                <T>Delete Vendor</T>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Column: Contact & Basic Info */}
        <div className="bg-white dark:bg-[#161b22] rounded-xl border border-gray-200 dark:border-[#30363d] p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-[#21262d]">
            <BuildingStorefrontIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-slate-300">
              <T>Contact Information</T>
            </h2>
          </div>

          <div className="space-y-3 text-xs">
            <div>
              <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-0.5"><T>Contact Person</T></span>
              <p className="font-semibold text-gray-800 dark:text-slate-200">{vendor.contactPerson || '—'}</p>
            </div>

            <div>
              <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-0.5 flex items-center gap-1">
                <EnvelopeIcon className="w-3.5 h-3.5" /> <T>Email Address</T>
              </span>
              <p className="font-medium text-gray-800 dark:text-slate-200">{vendor.email || '—'}</p>
            </div>

            <div>
              <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-0.5 flex items-center gap-1">
                <PhoneIcon className="w-3.5 h-3.5" /> <T>Phone Number</T>
              </span>
              <p className="font-medium text-gray-800 dark:text-slate-200">{vendor.phone || '—'}</p>
            </div>

            <div>
              <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-0.5 flex items-center gap-1">
                <MapPinIcon className="w-3.5 h-3.5" /> <T>Business Address</T>
              </span>
              <p className="text-gray-700 dark:text-slate-300 leading-relaxed whitespace-pre-line">{vendor.address || '—'}</p>
            </div>

            <div>
              <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-0.5"><T>Category</T></span>
              <span className="inline-block px-2.5 py-1 rounded bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-semibold text-[11px]">
                {vendor.category || 'General'}
              </span>
            </div>
          </div>
        </div>

        {/* Center Column: Legal & Compliance */}
        <div className="bg-white dark:bg-[#161b22] rounded-xl border border-gray-200 dark:border-[#30363d] p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-[#21262d]">
            <IdentificationIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-slate-300">
              <T>Legal & Compliance</T>
            </h2>
          </div>

          <div className="space-y-3 text-xs">
            <div>
              <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-0.5"><T>GST Number</T></span>
              <p className="font-mono font-medium text-gray-800 dark:text-slate-200">{vendor.gstNumber || '—'}</p>
            </div>

            <div>
              <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-0.5"><T>PAN Number</T></span>
              <p className="font-mono font-medium text-gray-800 dark:text-slate-200">{vendor.panNumber || '—'}</p>
            </div>

            <div>
              <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-0.5"><T>License Number</T></span>
              <p className="font-mono font-medium text-gray-800 dark:text-slate-200">{vendor.licenseNumber || '—'}</p>
            </div>

            <div>
              <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-0.5 flex items-center gap-1">
                <DocumentCheckIcon className="w-3.5 h-3.5" /> <T>Certificate Expiry</T>
              </span>
              <p className="font-semibold text-gray-800 dark:text-slate-200">
                {formatDate(vendor.certificateExpiry) || '—'}
              </p>
            </div>

            <div>
              <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-0.5"><T>Payment Terms</T></span>
              <p className="text-gray-700 dark:text-slate-300">{vendor.paymentTerms || 'Standard 30 Days'}</p>
            </div>
          </div>
        </div>

        {/* Right Column: Contract & Performance */}
        <div className="bg-white dark:bg-[#161b22] rounded-xl border border-gray-200 dark:border-[#30363d] p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-[#21262d]">
            <CalendarDaysIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-slate-300">
              <T>Contract & Performance</T>
            </h2>
          </div>

          <div className="space-y-3 text-xs">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-0.5"><T>Contract Start</T></span>
                <p className="font-semibold text-gray-800 dark:text-slate-200">
                  {formatDate(vendor.contractStart) || '—'}
                </p>
              </div>
              <div>
                <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-0.5"><T>Contract End</T></span>
                <p className="font-semibold text-gray-800 dark:text-slate-200">
                  {formatDate(vendor.contractEnd) || '—'}
                </p>
              </div>
            </div>

            <div>
              <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-1"><T>Vendor Rating</T></span>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  star <= rating ? (
                    <StarSolid key={star} className="w-4 h-4 text-amber-400" />
                  ) : (
                    <StarIcon key={star} className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                  )
                ))}
                <span className="ml-2 font-bold text-gray-700 dark:text-slate-300 text-xs">
                  {rating.toFixed(1)} / 5.0
                </span>
              </div>
            </div>

            <div>
              <span className="text-gray-400 dark:text-slate-500 block text-[11px] mb-0.5"><T>Performance Evaluation</T></span>
              <p className="text-gray-700 dark:text-slate-300 italic bg-gray-50 dark:bg-[#0d1117] p-2.5 rounded-lg border border-gray-200 dark:border-[#30363d] leading-relaxed">
                {vendor.performanceEvaluation || 'No performance notes recorded.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section: Assigned Projects & History */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Assigned Projects */}
        <div className="bg-white dark:bg-[#161b22] rounded-xl border border-gray-200 dark:border-[#30363d] p-5 shadow-xs space-y-3">
          <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-[#21262d]">
            <BriefcaseIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-slate-300">
              <T>Assigned Projects</T>
            </h2>
          </div>
          {vendor.assignedProjects && vendor.assignedProjects.length > 0 ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {vendor.assignedProjects.map((proj, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-gray-100 dark:bg-[#21262d] text-gray-800 dark:text-slate-300 rounded-lg text-xs font-medium border border-gray-200 dark:border-[#30363d]"
                >
                  {typeof proj === 'object' ? proj.name || proj.project_name : proj}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic pt-1"><T>No specific projects linked to this vendor.</T></p>
          )}
        </div>

        {/* Status History */}
        <div className="bg-white dark:bg-[#161b22] rounded-xl border border-gray-200 dark:border-[#30363d] p-5 shadow-xs space-y-3">
          <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-[#21262d]">
            <ShieldCheckIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-slate-300">
              <T>Status History</T>
            </h2>
          </div>
          {vendor.history && vendor.history.length > 0 ? (
            <ul className="space-y-2.5 pt-1 max-h-44 overflow-y-auto">
              {vendor.history.map((h, i) => (
                <li key={i} className="flex flex-col text-xs border-l-2 border-indigo-300 pl-3 py-0.5">
                  <span className="font-semibold text-gray-800 dark:text-slate-200">{h.message}</span>
                  <span className="text-gray-400 text-[10px]">{formatDate(h.date)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-gray-400 italic pt-1"><T>No status history recorded.</T></p>
          )}
        </div>
      </div>
    </div>
  );
}
